import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getWherebyCallDisplayName } from '../../../../../../pure-js/libs/isWherebyBotParticipant'
import { GamerRoutes } from '../../../../config/routes/routeEnums'
import useAppState from '../../../../hooks/useAppState'
import { useUser } from '../../../../hooks/useUser'
import { useGlobalWhereby } from '../../../../shared/hooks/useGlobalWhereby'
import { useMediaPermissions } from '../../../../shared/hooks/useMediaPermissions'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { getSprintCallTitle } from '../../../shared/helpers/liveActivityHelpers'
import { isCallMediaBlocked, resolveCallView } from '../../components/whereby/wherebyCallHelpers'
import { useSprintCallRequest } from './useSprintCallRequest'
import { useSprintCallRoom } from './useSprintCallRoom'

/** Everything the sprint call page shows: its title, which state the call is in, and the call itself. */
export const useSprintCallPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { killCall } = useGlobalWhereby()
  const { state: appState } = useAppState()
  const { data: user } = useUser()
  const mediaPermissions = useMediaPermissions()
  const request = useSprintCallRequest()
  const { sprintCall, ownership, watchdogs, hasFailedToLoad } = useSprintCallRoom(request)

  // This page holds the call, so the floating one leaves: otherwise the person would sit in two rooms.
  useEffect(() => {
    killCall()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // An address without a call leads back to the activities.
  useEffect(() => {
    if (!request) navigate(GamerRoutes.UPCOMING_ACTIVITIES, { replace: true })
  }, [request, navigate])

  const leave = useCallback(() => navigate(GamerRoutes.UPCOMING_ACTIVITIES), [navigate])

  const roomUrl = sprintCall?.room?.roomUrl
  const userId = appState.user?.id
  // The signed-in user stands in when the user read came back empty.
  const displayName = getWherebyCallDisplayName(user) || getWherebyCallDisplayName(appState.user)
  const isCallReady = ownership.canConnect && !mediaPermissions.isLoading && Boolean(roomUrl && userId && displayName)

  return {
    title: getSprintCallTitle(sprintCall?.activityType, t),
    callName: sprintCall?.sprintTitle ?? '',
    view: resolveCallView(ownership.status, isCallReady),
    isMediaBlocked: isCallMediaBlocked(mediaPermissions),
    isTakingTooLong: watchdogs.isPreparingTooLong || hasFailedToLoad,
    retryRoom: watchdogs.retryRoom,
    roomUrl,
    userId,
    displayName,
    ownership,
    leave
  }
}
